import { v4 as uuidv4 } from 'uuid';

import {
  CLOUD_API_NAME_IDENTITY,
  getClientConfigForEnv,
  type CloudApisUrlKey,
} from '@/client_config';
import {
  API_ENDPOINT_ROOTS,
  API_NAMES,
  ApiMap,
  ApiNameType,
  DEFAULT_EDGE_DNS_ZONE,
  type EdgeApiNameType,
  FEEDSTATION_APP_ROOT,
} from '@/constants';
import type { SiteConfig } from '@/stores';

import { capitalizeAndSplitOnUpperCase } from './string.ts';

/** Hosted (SaaS) API id — keys of bundled `client_config.apis`. */
export const isCloudApisUrlKey = (api: ApiNameType): api is CloudApisUrlKey =>
  Object.hasOwn(CLOUD_API_NAME_IDENTITY, api);

/**
 * Cloud-only key: a cloud API with no paired edge service (its `cloud`-stripped name is not in
 * `API_ENDPOINT_ROOTS`). These always resolve from bundled `client_config`, regardless of portable mode.
 */
export const isCloudOnlyApiKey = (api: ApiNameType): boolean => {
  if (!isCloudApisUrlKey(api)) return false;
  const tail = api.slice('cloud'.length);
  const derived = tail.charAt(0).toLowerCase() + tail.slice(1);
  return !(derived in API_ENDPOINT_ROOTS);
};

export const generateDefaultSite = (
  host: string = window.location.host,
  env?: string,
  portableMode?: boolean
) => {
  const parts = host.replace('scale-internal.', '').split('.');
  const clusterName = parts[1];

  const site = {
    name: capitalizeAndSplitOnUpperCase(clusterName.split('-')[1]),
    // Stand-in until the site list replaces it with the inventory site id.
    id: uuidv4(),
    // Everything after the app label, so the zone this cluster serves on is carried, not assumed.
    baseUrl: parts.slice(1).join('.'),
    includeScaleInternal: host.includes('scale-internal'),
  };
  return { ...site, apis: generateApis(site, env, portableMode) };
};

export const isLocalhost = (host = window.location.host) => {
  return host.includes('localhost');
};

export const generateApis = (site: SiteConfig, env?: string, portableMode?: boolean) => {
  return Object.fromEntries(
    (Object.keys(API_NAMES) as ApiNameType[]).map(api => [
      api,
      getApiUrl(api, site, env, portableMode),
    ])
  ) as ApiMap;
};

/**
 * Strip leading `cloud` and lowercase the first character (`cloudInventoryApi` → `inventoryApi`).
 * Cloud keys must be `cloud` + PascalCase edge name (e.g. `cloudUserconfigApi` → `userconfigApi`).
 */
const edgeApiNameFromCloudRoute = (cloud: CloudApisUrlKey): EdgeApiNameType => {
  const tail = cloud.slice('cloud'.length);
  const derived = (tail.charAt(0).toLowerCase() + tail.slice(1)) as EdgeApiNameType;
  if (!(derived in API_ENDPOINT_ROOTS)) {
    throw new Error(
      `edgeApiNameFromCloudRoute: "${cloud}" → "${String(derived)}" is not an edge API; align client_config.apis keys with API_ENDPOINT_ROOTS.`
    );
  }
  return derived;
};

const getApiUrl = (api: ApiNameType, site: SiteConfig, env?: string, portableMode?: boolean) => {
  const { apis, baseUrl, includeScaleInternal } = site;
  if (apis?.[api]) {
    return apis[api] as string;
  }
  if (isCloudApisUrlKey(api)) {
    if (!portableMode && !isCloudOnlyApiKey(api)) {
      return getApiUrl(edgeApiNameFromCloudRoute(api), site, env, portableMode);
    }
    return getClientConfigForEnv(env).apis[api].trim();
  }

  const rootApi = API_ENDPOINT_ROOTS[api];
  if (baseUrl) {
    return `https://${rootApi}.${getEdgeHost({ baseUrl, includeScaleInternal })}/${withHub(api)}`;
  }
  if (isLocalhost()) {
    const errorMessage =
      `Cannot resolve API URL for "${api}" while the SPA is served from localhost. ` +
      'defaultSite (env site object: name, id, baseUrl, includeScaleInternal, apis, …) must define baseUrl so edge URLs can be built, ' +
      `and/or define apis["${api}"] (and any other APIs you use) with full URLs—for example when proxying selected services to localhost.`;
    console.error(errorMessage);
    throw new Error(errorMessage);
  }

  const restOfHost = window.location.host.split('.').slice(1).join('.');
  return `${window.location.protocol}//${rootApi}.${restOfHost}/${withHub(api)}`;
};

const withHub = (api: ApiNameType) => (api === API_NAMES.bridgeApi ? 'hub' : '');

/**
 * Link to the site's Feedstation app. Always the public host, even when `includeScaleInternal` is
 * set: that flag only routes SiteControl's own API calls through Tailscale. Undefined on localhost
 * without a site `baseUrl`, where there is no edge host to build it from.
 */
export const getFeedstationUrl = (site?: SiteConfig) => {
  if (site?.baseUrl) {
    return `https://${FEEDSTATION_APP_ROOT}.${getEdgeHost({ baseUrl: site.baseUrl })}/`;
  }
  if (isLocalhost()) return undefined;

  const restOfHost = window.location.host
    .replace('scale-internal.', '')
    .split('.')
    .slice(1)
    .join('.');
  return `${window.location.protocol}//${FEEDSTATION_APP_ROOT}.${restOfHost}/`;
};

const getEdgeHost = ({
  baseUrl,
  includeScaleInternal = false,
}: {
  baseUrl: string;
  includeScaleInternal?: boolean;
}) => {
  const scaleInternal = includeScaleInternal ? 'scale-internal.' : '';
  // Site records written before the edge reported a full host hold only a cluster name.
  const host = baseUrl.includes('.') ? baseUrl : `${baseUrl}.${DEFAULT_EDGE_DNS_ZONE}`;
  return `${scaleInternal}${host}`;
};
